import React, { useState } from 'react';
import { CustomerOrder } from '../../types';
import {
  formatRupiah,
  createWhatsAppLink,
  formatOrderDateTime,
  getJakartaDateKey,
} from '../../utils/formatters';
import { resolvePersistedOrderServiceFee } from '../../utils/deliveryFee';
import {
  X,
  Clock,
  User,
  MapPin,
  MessageCircle,
  Eye,
  AlertCircle,
  CreditCard,
  ShoppingBag,
  FileText,
  Printer,
  Check,
  Coffee,
  CheckCircle2,
  Bell,
  Utensils,
  XCircle,
  Tag,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: CustomerOrder | null;
  isTodayOrder?: boolean;
  isUpdating?: boolean;
  onAcceptOrder?: (order: CustomerOrder) => void;
  onStartBrewing?: (order: CustomerOrder) => void;
  onMarkReady?: (order: CustomerOrder) => void;
  onCompleteOrder?: (order: CustomerOrder) => void;
  onRejectOrder?: (order: CustomerOrder) => void;
  onPrintSlip?: (order: CustomerOrder) => void;
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  order,
  isTodayOrder,
  isUpdating = false,
  onAcceptOrder,
  onStartBrewing,
  onMarkReady,
  onCompleteOrder,
  onRejectOrder,
  onPrintSlip,
}) => {
  const [zoomReceipt, setZoomReceipt] = useState<boolean>(false);

  if (!order) return null;

  const computedIsToday =
    isTodayOrder !== undefined
      ? isTodayOrder
      : getJakartaDateKey(order.createdAt) === getJakartaDateKey(new Date());

  const notifyWaText = `Halo Kak ${order.customerName}, pesanan LetonKu dengan nomor *${order.orderNumber}* saat ini berstatus: *${order.orderStatus}* (Status Pembayaran: *${order.paymentStatus}*). Terima kasih!`;
  const customerWaLink = order.customerPhone
    ? createWhatsAppLink(order.customerPhone, notifyWaText)
    : null;

  const pickupTimeDisplay = order.pickupTime || order.pickup_time || 'Belum ditentukan';

  // Stitch Ocean Roast Status Badge styling
  const renderOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'NEW':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#c6e7ff] text-[#004c6b] border border-[#9fd4ff]">
            <span className="w-2 h-2 rounded-full bg-[#006389] animate-ping" />
            <span>BARU (NEW)</span>
          </span>
        );
      case 'ACCEPTED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#dbe9ff] text-[#004c6b] border border-[#b8d4ff]">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#006389]" />
            <span>DITERIMA (ACCEPTED)</span>
          </span>
        );
      case 'PREPARING':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#ffddb8] text-[#653e00] border border-[#f5c38c]">
            <Coffee className="w-3.5 h-3.5 animate-pulse" />
            <span>DIPROSES (PREPARING)</span>
          </span>
        );
      case 'READY':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#d1fae5] text-[#065f46] border border-[#a7f3d0]">
            <Bell className="w-3.5 h-3.5" />
            <span>SIAP DIAMBIL (READY)</span>
          </span>
        );
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#eef4ff] text-[#041d32] border border-[#c6d7e8]">
            <Check className="w-3.5 h-3.5 text-[#006389]" />
            <span>SELESAI (COMPLETED)</span>
          </span>
        );
      case 'CANCELLED':
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-[#ffdad6] text-[#93000a] border border-[#ffb4ab]">
            <XCircle className="w-3.5 h-3.5" />
            <span>DIBATALKAN (CANCELLED)</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
            {status}
          </span>
        );
    }
  };

  // Stitch Ocean Roast Payment Status Badge styling
  const renderPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'PAID':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-[#d1fae5] text-[#065f46] border border-[#a7f3d0]">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>LUNAS (PAID)</span>
          </span>
        );
      case 'WAITING VERIFICATION':
      case 'WAITING_VERIFICATION':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-[#ffddb8] text-[#653e00] border border-[#f5c38c]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#825100] animate-pulse" />
            <span>MENUNGGU VERIFIKASI</span>
          </span>
        );
      case 'WAITING PAYMENT':
      case 'WAITING_PAYMENT':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <span>MENUNGGU PEMBAYARAN</span>
          </span>
        );
      case 'PAYMENT REJECTED':
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-[#ffdad6] text-[#93000a] border border-[#ffb4ab]">
            <X className="w-3.5 h-3.5" />
            <span>DITOLAK</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
            {status}
          </span>
        );
    }
  };

  const totalItemsQty = (order?.items || []).reduce((sum, it) => sum + (it?.quantity || 0), 0);
  const discountVal = Number(order?.discountAmount || 0);
  const hasVoucher = !!(order?.voucherCode || discountVal > 0);
  const itemsSubtotal = (order?.items || []).reduce((sum, it) => {
    if (!it) return sum;
    const unitPrice = Number(it.unitPrice ?? it.price ?? 0);
    return sum + (unitPrice * (it.quantity || 1));
  }, 0);
  const serviceFeeVal = resolvePersistedOrderServiceFee(order);
  const deliveryFeeVal = order?.orderType === 'DELIVERY' ? Number(order?.deliveryFee ?? order?.delivery_fee ?? 0) : 0;
  const subtotalBeforeDiscount = itemsSubtotal > 0 ? itemsSubtotal : Math.max(0, (order?.totalAmount || 0) + discountVal - deliveryFeeVal - serviceFeeVal);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          id="order-detail-modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-[#041d32]/65 backdrop-blur-xs"
          onClick={(e) => {
            if ((e.target as HTMLElement).id === 'order-detail-modal-overlay') {
              onClose();
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="w-full max-w-2xl bg-[#f8fbff] border border-[#dbe9ff] rounded-2xl shadow-[0_20px_50px_rgba(4,29,50,0.25)] flex flex-col max-h-[92vh] overflow-hidden text-[#041d32]"
          >
            {/* 1. Top Header (Stitch Detail Pesanan Design) */}
            <div className="px-5 py-4 border-b border-[#dbe9ff] flex items-center justify-between bg-white shrink-0">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#eef4ff] text-[#006389] border border-[#dbe9ff]">
                    {computedIsToday ? 'PESANAN HARI INI' : 'ARSIP RIWAYAT'}
                  </span>
                  <h3 className="font-display font-black text-[#041d32] text-lg sm:text-xl tracking-tight">
                    {order.orderNumber.startsWith('#') ? order.orderNumber : `#${order.orderNumber}`}
                  </h3>
                </div>
                <p className="text-xs text-[#52606d] mt-1 font-mono flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-[#006389]" />
                  <span>{formatOrderDateTime(order.createdAt)}</span>
                </p>
              </div>

              <div className="flex items-center gap-2">
                {onPrintSlip && (
                  <button
                    type="button"
                    onClick={() => onPrintSlip(order)}
                    className="px-3 py-2 rounded-xl bg-[#eef4ff] hover:bg-[#dbe9ff] text-[#006389] font-bold text-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                    title="Cetak Slip Dapur"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden sm:inline">Cetak Slip</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-[#52606d] hover:text-[#041d32] hover:bg-[#eef4ff] rounded-xl transition-colors cursor-pointer"
                  title="Tutup"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Body Content */}
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              {/* Section 1: Status Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white p-3.5 rounded-xl border border-[#dbe9ff] shadow-[0_2px_6px_rgba(0,99,137,0.03)] flex flex-col justify-between gap-2">
                  <span className="text-[11px] font-bold text-[#52606d] uppercase tracking-wider">
                    Status Pesanan
                  </span>
                  <div>{renderOrderStatusBadge(order.orderStatus)}</div>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-[#dbe9ff] shadow-[0_2px_6px_rgba(0,99,137,0.03)] flex flex-col justify-between gap-2">
                  <span className="text-[11px] font-bold text-[#52606d] uppercase tracking-wider">
                    Status Pembayaran ({order.paymentMethod || 'QRIS'})
                  </span>
                  <div>{renderPaymentStatusBadge(order.paymentStatus)}</div>
                </div>
              </div>

              {/* Section 2: Informasi Pelanggan, Outlet & Pengambilan */}
              <div className="bg-white p-4 rounded-xl border border-[#dbe9ff] shadow-[0_2px_6px_rgba(0,99,137,0.03)] space-y-3">
                <h4 className="text-xs font-extrabold text-[#006389] uppercase tracking-wider flex items-center gap-1.5 border-b border-[#f1f5f9] pb-2">
                  <User className="w-4 h-4" />
                  <span>Informasi Pelanggan & Pengambilan</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {/* Nama Pelanggan */}
                  <div className="p-3 rounded-xl bg-[#f8fbff] border border-[#e4efff]">
                    <span className="text-[11px] font-semibold text-[#52606d] uppercase block mb-0.5">
                      Nama Pelanggan
                    </span>
                    <span className="font-bold text-[#041d32] text-sm">
                      {order.customerName || '-'}
                    </span>
                  </div>

                  {/* WhatsApp + Kirim Pesan */}
                  <div className="p-3 rounded-xl bg-[#f8fbff] border border-[#e4efff]">
                    <span className="text-[11px] font-semibold text-[#52606d] uppercase block mb-0.5">
                      Nomor HP / WhatsApp
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[#041d32] text-xs font-bold">
                        {order.customerPhone || '-'}
                      </span>
                      {customerWaLink && (
                        <a
                          href={customerWaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg border border-emerald-200 transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          <span>Hubungi</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Outlet & Tipe Order */}
                  <div className="p-3 rounded-xl bg-[#f8fbff] border border-[#e4efff]">
                    <span className="text-[11px] font-semibold text-[#52606d] uppercase block mb-0.5">
                      Outlet & Layanan
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-[#041d32] text-xs flex items-center gap-1 truncate">
                        <MapPin className="w-3.5 h-3.5 text-[#006389] shrink-0" />
                        <span className="truncate">{order.outletName || order.outletId || '-'}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#eef4ff] text-[#006389] text-[11px] font-bold shrink-0">
                        {order.orderType === 'DINE IN' ? (
                          <>
                            <Utensils className="w-3 h-3" />
                            <span>Dine In {order.tableNumber ? `• Meja ${order.tableNumber}` : ''}</span>
                          </>
                        ) : order.orderType === 'DELIVERY' ? (
                          <>
                            <MapPin className="w-3 h-3" />
                            <span>Delivery</span>
                          </>
                        ) : (
                          <>
                            <ShoppingBag className="w-3 h-3" />
                            <span>Take Away</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Jam Pengambilan */}
                  <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200/80">
                    <span className="text-[11px] font-bold text-amber-800 uppercase block mb-0.5">
                      {order.orderType === 'DELIVERY' ? 'Waktu Pemesanan' : 'Waktu Pengambilan'}
                    </span>
                    <span className="font-mono font-black text-amber-950 flex items-center gap-1.5 text-xs">
                      <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span>{pickupTimeDisplay}</span>
                    </span>
                  </div>
                </div>

                {/* Delivery Info Section (Only for DELIVERY orders) */}
                {order.orderType === 'DELIVERY' && (
                  <div className="mt-3 p-3.5 rounded-xl bg-[#f0f9ff] border border-[#bae6fd] space-y-2 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-extrabold text-[#0369a1] uppercase tracking-wider flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>Informasi Pengiriman (Delivery)</span>
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded-md bg-white border border-[#bae6fd] text-[#0369a1] font-bold text-[11px]">
                          Status: {order.deliveryStatus || order.delivery_status || 'UNASSIGNED'}
                        </span>
                        {(order.courierUsername || order.courier_username) && (
                          <span className="px-2 py-0.5 rounded-md bg-amber-100 border border-amber-300 text-amber-900 font-bold text-[11px]">
                            Kurir: {order.courierUsername || order.courier_username}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-[#0c4a6e] font-semibold leading-relaxed">
                      {order.deliveryAddress || order.delivery_address || 'Alamat pengiriman tidak tercantum'}
                    </p>
                    {typeof (order.deliveryDistance ?? order.delivery_distance) === 'number' && (
                      <div className="text-[11px] text-[#0369a1] font-medium">
                        Estimasi Jarak: <strong>{order.deliveryDistance ?? order.delivery_distance} km</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 3: Catatan Customer */}
              {order.customerNote && order.customerNote.trim().length > 0 && (
                <div className="p-3.5 rounded-xl bg-[#fff8eb] border border-[#f5d08c]">
                  <div className="flex items-center gap-1.5 text-[#825100] text-xs font-extrabold uppercase tracking-wider mb-1">
                    <FileText className="w-3.5 h-3.5" />
                    <span>Catatan Pelanggan</span>
                  </div>
                  <p className="text-sm font-semibold text-[#4a2c00] italic">
                    "{order.customerNote}"
                  </p>
                </div>
              )}

              {/* Section 4: Rincian Item Pesanan */}
              <div className="bg-white p-4 rounded-xl border border-[#dbe9ff] shadow-[0_2px_6px_rgba(0,99,137,0.03)] space-y-3">
                <div className="flex items-center justify-between border-b border-[#f1f5f9] pb-2">
                  <h4 className="text-xs font-extrabold text-[#006389] uppercase tracking-wider flex items-center gap-1.5">
                    <ShoppingBag className="w-4 h-4" />
                    <span>Daftar Item Pesanan</span>
                  </h4>
                  <span className="text-xs font-bold text-[#041d32] bg-[#eef4ff] px-2.5 py-0.5 rounded-full">
                    {totalItemsQty} Item
                  </span>
                </div>

                <div className="divide-y divide-[#f1f5f9]">
                  {(order.items || []).map((it, idx) => {
                    const sizePrice = it.size?.price || 0;
                    const topPrice = it.topping?.price || 0;
                    const syrPrice = it.syrup?.price || 0;
                    const customsPrice = (it.customOptions || []).reduce(
                      (sum, c) => sum + (c.price || 0),
                      0
                    );
                    const unitPrice = Number(it.unitPrice ?? it.price ?? 0);
                    const itemSubtotal = unitPrice * (it.quantity || 1);

                    const isDummyTopping = !it.topping || !it.topping.name || ['No Topping', 'Tanpa Topping', 'Tanpa Toppings', 'None'].includes(it.topping.name) || (it.topping.id === 'top-no-topping' && (it.topping.price || 0) === 0);
                    const isDummySyrup = !it.syrup || !it.syrup.name || ['No Syrup', 'Normal (Standard Recipe)', 'Normal', 'Tanpa Syrup', 'None'].includes(it.syrup.name) || (it.syrup.id === 'syr-no-syrup' && (it.syrup.price || 0) === 0);

                    const hasCustomizations =
                      (it.size && it.size.name) ||
                      (!isDummyTopping) ||
                      (!isDummySyrup) ||
                      (it.customOptions && it.customOptions.length > 0) ||
                      it.note;

                    return (
                      <div
                        key={idx}
                        className="py-3 first:pt-1 last:pb-1 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <span className="font-mono text-xs font-extrabold text-[#006389] bg-[#eef4ff] px-2 py-1 rounded-lg border border-[#dbe9ff] shrink-0">
                            {it.quantity}x
                          </span>
                          <div className="min-w-0">
                            <h5 className="font-bold text-[#041d32] text-sm leading-snug">
                              {it.name}
                            </h5>
                            <span className="text-[11px] font-mono text-[#52606d] block mt-0.5">
                              @ {formatRupiah(unitPrice)}
                            </span>
                            {hasCustomizations && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {it.size && it.size.name && (
                                  <span className="text-[11px] bg-[#f8fbff] text-[#3e484f] px-2 py-0.5 rounded-md border border-[#e4efff]">
                                    Size: <strong>{it.size.name}</strong>{' '}
                                    {sizePrice > 0 ? `(+${formatRupiah(sizePrice)})` : ''}
                                  </span>
                                )}
                                {!isDummyTopping && it.topping && (
                                  <span className="text-[11px] bg-[#f8fbff] text-[#3e484f] px-2 py-0.5 rounded-md border border-[#e4efff]">
                                    Topping: <strong>{it.topping.name}</strong> (+
                                    {formatRupiah(topPrice)})
                                  </span>
                                )}
                                {!isDummySyrup && it.syrup && (
                                  <span className="text-[11px] bg-[#f8fbff] text-[#3e484f] px-2 py-0.5 rounded-md border border-[#e4efff]">
                                    Syrup: <strong>{it.syrup.name}</strong> (+
                                    {formatRupiah(syrPrice)})
                                  </span>
                                )}
                                {it.customOptions &&
                                  it.customOptions.length > 0 &&
                                  it.customOptions.map((co, cidx) => (
                                    <span
                                      key={cidx}
                                      className="text-[11px] bg-[#f8fbff] text-[#3e484f] px-2 py-0.5 rounded-md border border-[#e4efff]"
                                    >
                                      {co.groupName ? `${co.groupName}: ` : ''}<strong>{co.optionName}</strong>{' '}
                                      {co.price > 0 ? `(+${formatRupiah(co.price)})` : ''}
                                    </span>
                                  ))}
                              </div>
                            )}
                            {it.note && (
                              <p className="text-xs text-[#825100] italic bg-[#fff8eb] px-2.5 py-1 rounded-lg border border-[#f5d08c] mt-1.5">
                                Catatan: "{it.note}"
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="font-mono text-sm font-extrabold text-[#041d32] shrink-0">
                          {formatRupiah(itemSubtotal)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Subtotal & Total Summary */}
                <div className="pt-3 border-t border-[#dbe9ff] space-y-2">
                  <div className="flex justify-between text-xs text-[#52606d] font-medium">
                    <span>Subtotal Pesanan ({totalItemsQty} item)</span>
                    <span className="font-mono font-semibold text-[#041d32]">
                      {formatRupiah(subtotalBeforeDiscount)}
                    </span>
                  </div>

                  {hasVoucher && (
                    <div className="flex justify-between items-center text-xs text-emerald-800 bg-emerald-50 px-3 py-2 rounded-xl border border-emerald-200/80">
                      <div className="flex items-center gap-1.5 font-bold">
                        <Tag className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>Voucher Diskon ({order.voucherCode || 'Terpakai'})</span>
                      </div>
                      <span className="font-mono font-black text-emerald-700">
                        - {formatRupiah(discountVal)}
                      </span>
                    </div>
                  )}

                  {order.orderType === 'DELIVERY' && (
                    <div className="flex justify-between items-center text-xs text-[#52606d] font-medium">
                      <span>Ongkos Kirim (Delivery)</span>
                      <span className="font-mono font-semibold text-[#041d32]">
                        {formatRupiah(deliveryFeeVal)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-xs text-[#52606d] font-medium">
                    <span>Biaya Layanan</span>
                    <span className="font-mono font-semibold text-[#041d32]">
                      {formatRupiah(serviceFeeVal)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-[#f1f5f9]">
                    <span className="text-sm font-extrabold text-[#041d32] uppercase tracking-wide">
                      Total Pembayaran
                    </span>
                    <span className="font-mono font-black text-lg text-[#006389]">
                      {formatRupiah(order.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 5: Informasi Pembayaran & Bukti QRIS */}
              <div className="bg-white p-4 rounded-xl border border-[#dbe9ff] shadow-[0_2px_6px_rgba(0,99,137,0.03)] space-y-3">
                <h4 className="text-xs font-extrabold text-[#006389] uppercase tracking-wider flex items-center gap-1.5 border-b border-[#f1f5f9] pb-2">
                  <CreditCard className="w-4 h-4" />
                  <span>Verifikasi & Bukti Pembayaran</span>
                </h4>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-2.5 rounded-lg bg-[#f8fbff] border border-[#e4efff]">
                    <span className="text-[#52606d] block mb-0.5">Metode Pembayaran</span>
                    <strong className="text-[#041d32] text-sm font-extrabold">
                      {order.paymentMethod || 'QRIS'}
                    </strong>
                  </div>
                  <div className="p-2.5 rounded-lg bg-[#f8fbff] border border-[#e4efff]">
                    <span className="text-[#52606d] block mb-0.5">Status Transaksi</span>
                    <strong className="text-[#041d32] text-xs font-bold">
                      {order.paymentStatus}
                    </strong>
                  </div>
                </div>

                {order.paymentMethod === 'QRIS' && (
                  <div>
                    {order.paymentReceiptUrl ? (
                      <div className="flex items-center justify-between p-3 rounded-xl bg-[#f8fbff] border border-[#dbe9ff]">
                        <div className="flex items-center gap-3">
                          <div
                            onClick={() => setZoomReceipt(true)}
                            className="w-12 h-12 rounded-lg bg-white border border-[#c6d7e8] overflow-hidden cursor-pointer relative group shrink-0 shadow-2xs"
                          >
                            <img
                              src={order.paymentReceiptUrl}
                              alt="Bukti Transfer"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-[#041d32] block">
                              Bukti Transfer QRIS Terlampir
                            </span>
                            <span className="text-[11px] text-emerald-700 font-medium">
                              Klik untuk memperbesar gambar
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setZoomReceipt(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#006389] hover:bg-[#004c6b] px-3 py-2 rounded-xl transition-colors cursor-pointer shadow-2xs"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Lihat Bukti</span>
                        </button>
                      </div>
                    ) : (
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-slate-400 shrink-0" />
                        <span>Bukti transfer belum diunggah oleh pelanggan.</span>
                      </div>
                    )}
                  </div>
                )}

                {order.rejectionReason && (
                  <div className="p-3 rounded-xl bg-[#ffdad6]/50 border border-[#ffb4ab] text-[#93000a] text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-[#ba1a1a] shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold">Alasan Pembatalan / Penolakan:</strong>
                      <span>{order.rejectionReason}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 6. Modal Footer Controls (Operational Actions for Today's Orders) */}
            <div className="px-5 py-3.5 border-t border-[#dbe9ff] bg-white flex flex-wrap items-center justify-between gap-2.5 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] text-[#041d32] font-bold text-xs border border-[#c6d7e8] transition-colors cursor-pointer"
              >
                Tutup
              </button>

              {computedIsToday && (
                <div className="flex flex-wrap items-center gap-2">
                  {/* Tolak Button */}
                  {onRejectOrder &&
                    order.orderStatus !== 'CANCELLED' &&
                    order.orderStatus !== 'COMPLETED' && (
                      <button
                        type="button"
                        disabled={isUpdating}
                        onClick={() => onRejectOrder(order)}
                        className="px-3.5 py-2 rounded-xl border border-[#ffdad6] bg-[#fff8f7] text-[#ba1a1a] text-xs font-bold hover:bg-[#ffdad6] transition-colors cursor-pointer"
                      >
                        Tolak Pesanan
                      </button>
                    )}

                  {/* Workflow Action Button */}
                  {order.orderStatus === 'NEW' && onAcceptOrder && (
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={() => onAcceptOrder(order)}
                      className="px-4 py-2 rounded-xl bg-[#006389] hover:bg-[#004f6e] text-white text-xs font-bold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" />
                      <span>Terima Pesanan</span>
                    </button>
                  )}

                  {order.orderStatus === 'ACCEPTED' && onStartBrewing && (
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={() => onStartBrewing(order)}
                      className="px-4 py-2 rounded-xl bg-[#006389] hover:bg-[#004f6e] text-white text-xs font-bold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <Coffee className="w-4 h-4" />
                      <span>Proses Pesanan</span>
                    </button>
                  )}

                  {order.orderStatus === 'PREPARING' && onMarkReady && (
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={() => onMarkReady(order)}
                      className="px-4 py-2 rounded-xl bg-[#006389] hover:bg-[#004f6e] text-white text-xs font-bold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Tandai Siap Diambil</span>
                    </button>
                  )}

                  {order.orderStatus === 'READY' && onCompleteOrder && (
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={() => onCompleteOrder(order)}
                      className="px-4 py-2 rounded-xl bg-[#041d32] hover:bg-[#1a3349] text-white text-xs font-bold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span>Selesaikan Pesanan</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Embedded Zoom Receipt Lightbox Modal */}
      {zoomReceipt && order.paymentReceiptUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-lg bg-white border border-[#dbe9ff] rounded-2xl p-4 shadow-2xl flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-[#041d32]">
                Bukti Pembayaran — {order.orderNumber}
              </span>
              <button
                type="button"
                onClick={() => setZoomReceipt(false)}
                className="p-1.5 text-[#52606d] hover:text-[#041d32] rounded-full hover:bg-[#eef4ff] cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-auto max-h-[70vh] bg-[#f8fbff] p-2 rounded-xl border border-[#dbe9ff] flex items-center justify-center">
              <img
                src={order.paymentReceiptUrl}
                alt="Struk Transfer"
                className="max-h-[60vh] object-contain rounded-lg shadow-xs"
              />
            </div>
            <button
              type="button"
              onClick={() => setZoomReceipt(false)}
              className="w-full py-2.5 rounded-xl bg-[#041d32] hover:bg-[#006389] text-white font-bold text-xs uppercase tracking-wider cursor-pointer transition-colors"
            >
              Tutup Bukti
            </button>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};
